import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Globe, Scale, Package, TrendingUp, Truck, DollarSign } from 'lucide-react';
import { services, images, companyInfo } from '../mockData';

const iconMap = {
  Globe: Globe,
  Scale: Scale,
  Package: Package,
  TrendingUp: TrendingUp,
  Truck: Truck,
  DollarSign: DollarSign
};

const Home = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    setIsVisible(true);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: 'var(--bg-primary)'
      }}>
        {/* Parallax Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${images.hero.swissAlps})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.5}px)`,
            transition: 'transform 0.1s ease-out'
          }} />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.7))'
          }} />
        </div>

        {/* Hero Content */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          padding: '0 7.6923%',
          maxWidth: '1200px',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 1s ease-out'
        }}>
          {/* Gold Line */}
          <div style={{
            width: '100px',
            height: '2px',
            background: 'var(--brand-primary)',
            margin: '0 auto 40px',
            animation: isVisible ? 'drawLine 1.5s ease-out' : 'none'
          }} />
          
          <h1 className="display-huge" style={{
            marginBottom: '30px',
            color: 'var(--text-primary)'
          }}>
            Swiss Excellence in
            <br />
            <span style={{ color: 'var(--brand-primary)' }}>International Trade</span>
          </h1>
          
          <p className="body-large" style={{
            marginBottom: '50px',
            maxWidth: '800px',
            margin: '0 auto 50px',
            color: 'var(--text-secondary)'
          }}>
            Elevating global commerce through precision, expertise, and unwavering commitment to excellence. Your strategic partner in navigating the complexities of international markets.
          </p>
          
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              className="btn-primary"
              onClick={() => navigate('/services')}
            >
              Explore Our Services
              <ArrowRight size={20} />
            </button>
            <button 
              className="btn-secondary"
              onClick={() => navigate('/contact')}
            >
              Get in Touch
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          animation: 'fadeInUp 1s ease-out 1.5s both'
        }}>
          <div style={{
            width: '2px',
            height: '60px',
            background: 'linear-gradient(to bottom, transparent, var(--brand-primary))',
            margin: '0 auto'
          }} />
        </div>
      </section>

      {/* Services Preview Section */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Section Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '80px'
          }}>
            <h2 className="display-large" style={{
              marginBottom: '20px'
            }}>
              Comprehensive Trade Solutions
            </h2>
            <p className="body-medium" style={{
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              From market access to logistics management, we provide end-to-end solutions tailored to your international trade needs.
            </p>
          </div>

          {/* Services Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px',
            marginBottom: '60px'
          }}>
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    padding: '40px',
                    transition: 'all 0.4s ease',
                    cursor: 'pointer',
                    opacity: 0,
                    animation: `fadeInUp 0.8s ease-out ${index * 0.1}s forwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(212, 175, 55, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-subtle)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => navigate('/services')}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'rgba(212, 175, 55, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px'
                  }}>
                    <Icon size={32} color="var(--brand-primary)" strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="heading-2" style={{
                    marginBottom: '16px'
                  }}>
                    {service.title}
                  </h3>
                </div>
              );
            })}
          </div>

          {/* More Info Button */}
          <div style={{
            textAlign: 'center'
          }}>
            <button 
              className="btn-primary"
              onClick={() => navigate('/services')}
            >
              More Information About Our Services
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-secondary)',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: '1px solid var(--border-subtle)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '60px',
          textAlign: 'center'
        }}>
          <div>
            <h3 className="display-medium" style={{
              color: 'var(--brand-primary)',
              marginBottom: '12px'
            }}>35+</h3>
            <p className="body-medium">Years of Excellence</p>
          </div>
          <div>
            <h3 className="display-medium" style={{
              color: 'var(--brand-primary)',
              marginBottom: '12px'
            }}>100%</h3>
            <p className="body-medium">Swiss Precision</p>
          </div>
          <div>
            <h3 className="display-medium" style={{
              color: 'var(--brand-primary)',
              marginBottom: '12px'
            }}>Global</h3>
            <p className="body-medium">Market Reach</p>
          </div>
          <div>
            <h3 className="display-medium" style={{
              color: 'var(--brand-primary)',
              marginBottom: '12px'
            }}>24/7</h3>
            <p className="body-medium">Expert Support</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;